import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { BUTTON_BORDER_RADIUS } from 'constants/borderRadius';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircleIcon from '@mui/icons-material/Circle';
import { CONSENT_WARNINGS, CONSENT_SUBITEMS } from 'constants/constants';

/**
 * @typedef {object} ConsentDialogProps
 * @property {boolean} open - Whether dialog is open
 * @property {Function} onAccept - Callback when user accepts consent
 * @property {Function} onDecline - Callback when user declines consent
 * @property {boolean} [isLoading=false] - Shows loading state on accept button
 */

/**
 * Government system usage consent dialog (FR-2.3.1).
 * Must be accepted for email/password authentication to proceed.
 *
 * @param {ConsentDialogProps} props - Component props
 * @returns {React.ReactElement}
 */
const ConsentDialog = ({ open, onAccept, onDecline, isLoading = false }) => {
    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            aria-labelledby="consent-dialog-title"
        >
            <DialogTitle id="consent-dialog-title" sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        U.S. Government System Notice
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <List dense>
                    {/* First two warnings */}
                    {CONSENT_WARNINGS.slice(0, 2).map((warning, index) => (
                        <ListItem key={index} sx={{ py: 0.75, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <CircleIcon sx={{ fontSize: 8 }} color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary={warning}
                                slotProps={{
                                    primaryTypography: {
                                        variant: 'body2',
                                    },
                                }}
                            />
                        </ListItem>
                    ))}

                    {/* Third warning with subitems */}
                    <ListItem sx={{ py: 0.75, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                            <CircleIcon sx={{ fontSize: 8 }} color="warning" />
                        </ListItemIcon>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                                {CONSENT_WARNINGS[2]}
                            </Typography>

                            <List dense sx={{ pl: 0, pt: 0.5 }}>
                                {CONSENT_SUBITEMS.map((subitem, subIndex) => (
                                    <ListItem key={subIndex} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                                        <ListItemIcon sx={{ minWidth: 20, mt: 0.5 }}>
                                            <CircleIcon sx={{ fontSize: 6 }} color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={subitem}
                                            slotProps={{
                                                primaryTypography: {
                                                    variant: 'body2',
                                                    color: 'text.secondary',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </ListItem>
                </List>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    id="consent-decline-btn"
                    onClick={onDecline}
                    color="error"
                    variant="outlined"
                    disabled={isLoading}
                    sx={{ borderRadius: BUTTON_BORDER_RADIUS, minWidth: 100 }}
                >
                    Decline
                </Button>
                <Button
                    id="consent-accept-btn"
                    onClick={onAccept}
                    color="primary"
                    variant="contained"
                    autoFocus
                    disabled={isLoading}
                    sx={{ borderRadius: BUTTON_BORDER_RADIUS, minWidth: 100 }}
                >
                    Accept
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ConsentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onDecline: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
};

export default ConsentDialog;
